import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Plus, Trash2, Upload, ClipboardList, ScrollText, Database,
  X, Info, Check,
} from 'lucide-react';
import api from '../lib/api';
import { formatDateTime } from '../lib/constants';
import MissionShell from '../components/MissionShell';
import { MISSIONS } from '../lib/missions';

const AUDIT_ENTITY_LABELS = {
  user: 'Usuario', organization: 'Organización', diagnostic: 'Diagnóstico',
  action_task: 'Tarea', treatment_activity: 'RAT', document: 'Documento',
  invitation: 'Invitación', news: 'Noticia', data_system: 'Sistema',
  traz_event: 'Trazabilidad',
};

const QUICK_DESCRIPTIONS = [
  'Acceso a ficha de empleado',
  'Entrega de expediente físico',
  'Exportación de listado de clientes',
  'Destrucción de documentos',
  'Solicitud ARCO+ atendida',
  'Modificación masiva de datos',
  'Copia de seguridad exportada',
  'Envío de datos a proveedor externo',
];

export default function Trazabilidad() {
  const [tab, setTab] = useState('interno');
  const [stats, setStats] = useState(null);
  useEffect(() => {
    Promise.all([api.get('/trazabilidad/events?source=manual'), api.get('/trazabilidad/events?source=csv')])
      .then(([m, c]) => setStats({ manual: m.data.length, csv: c.data.length }))
      .catch(() => setStats({ manual: 0, csv: 0 }));
  }, []);

  return (
    <MissionShell
      mission={MISSIONS.trazabilidad}
      idPrefix="traz-mission"
      doHint="Priviax registra automáticamente lo que se hace en la plataforma. Agrega aquí los eventos que ocurren fuera de ella (papel, exportaciones, otros sistemas)."
      evidence={[
        { label: 'Registro interno', value: 'Automático', tone: 'ok', detail: 'Acciones dentro de Priviax' },
        { label: 'Eventos manuales', value: stats?.manual ?? '—', tone: 'ok' },
        { label: 'Eventos importados', value: stats?.csv ?? '—', tone: 'ok', detail: 'Desde CSV de otros sistemas' },
      ]}
      evidenceNote="La bitácora completa se incluye en tu Carpeta de evidencias."
    >
    <div className="space-y-6" data-testid="trazabilidad-page">
      <div className="border-b border-[#E5E7EB]">
        <div className="flex gap-1 -mb-px" data-testid="traz-tabs">
          <TabBtn active={tab === 'interno'} onClick={() => setTab('interno')} icon={ScrollText} testid="tab-interno">
            Registro interno
          </TabBtn>
          <TabBtn active={tab === 'manual'} onClick={() => setTab('manual')} icon={ClipboardList} testid="tab-manual">
            Eventos manuales
          </TabBtn>
          <TabBtn active={tab === 'csv'} onClick={() => setTab('csv')} icon={Upload} testid="tab-csv">
            Importar CSV
          </TabBtn>
        </div>
      </div>

      {tab === 'interno' && <InternoTab />}
      {tab === 'manual' && <ManualTab />}
      {tab === 'csv' && <CsvTab />}
    </div>
    </MissionShell>
  );
}

function TabBtn({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex items-center gap-2 px-4 py-2.5 text-[13px] border-b-2 transition-colors ${
        active ? 'border-[#143A34] text-[#1C2329] font-medium' : 'border-transparent text-[#6B7280] hover:text-[#374151]'
      }`}>
      <Icon size={14} strokeWidth={1.5} /> {children}
    </button>
  );
}

/* -------------------- Tab 1: Registro interno (auto) -------------------- */

function InternoTab() {
  const [data, setData] = useState({ logs: [], entity_types: [] });
  const [loading, setLoading] = useState(true);
  const [filterEntity, setFilterEntity] = useState('todas');
  const [q, setQ] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterEntity !== 'todas') params.set('entity_type', filterEntity);
      if (q) params.set('q', q);
      const { data } = await api.get(`/audit/logs?${params.toString()}`);
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filterEntity]);

  const groupedByDay = useMemo(() => {
    const groups = {};
    data.logs.forEach((l) => {
      const day = l.created_at.slice(0, 10);
      (groups[day] = groups[day] || []).push(l);
    });
    return Object.entries(groups);
  }, [data.logs]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[14px] font-medium text-[#1C2329]">
            {data.logs.length} evento{data.logs.length === 1 ? '' : 's'} registrado{data.logs.length === 1 ? '' : 's'} automáticamente
          </div>
          <div className="text-[12.5px] text-[#6B7280]">
            Acciones ejecutadas dentro de Priviax. Registro inmutable para fiscalización.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input className="pv-input max-w-xs" placeholder="Buscar en registros…" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()} data-testid="interno-search" />
          <button className="pv-btn-secondary text-[13px]" onClick={load}>Buscar</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        <FilterChip active={filterEntity === 'todas'} onClick={() => setFilterEntity('todas')}>Todas</FilterChip>
        {data.entity_types.map((e) => (
          <FilterChip key={e} active={filterEntity === e} onClick={() => setFilterEntity(e)}>{AUDIT_ENTITY_LABELS[e] || e}</FilterChip>
        ))}
      </div>

      {loading ? <div className="text-sm text-[#6B7280]">Cargando…</div> :
       data.logs.length === 0 ? (
        <div className="border border-[#E5E7EB] bg-white p-10 text-center text-[#6B7280]">Sin eventos con esos filtros.</div>
      ) : (
        <div className="border border-[#E5E7EB] bg-white divide-y divide-[#E5E7EB]">
          {groupedByDay.map(([day, logs]) => (
            <div key={day}>
              <div className="px-6 py-3 bg-[#FAFBFC] flex items-center justify-between">
                <span className="text-[12px] font-mono uppercase tracking-wider text-[#374151]">{day}</span>
                <span className="text-[11px] text-[#9CA3AF]">{logs.length} evento{logs.length === 1 ? '' : 's'}</span>
              </div>
              {logs.map((l) => (
                <div key={l.id} className="px-6 py-3 hover:bg-[#FAFBFC] grid grid-cols-[100px_1fr_auto] gap-4 items-start" data-testid={`log-${l.id}`}>
                  <div className="text-[11px] font-mono text-[#6B7280]">{l.created_at.slice(11, 19)}</div>
                  <div>
                    <div className="text-[13.5px] text-[#1C2329]"><span className="font-medium">{l.action}</span> <span className="text-[#6B7280]">· {AUDIT_ENTITY_LABELS[l.entity_type] || l.entity_type}</span></div>
                    <div className="text-[11.5px] text-[#6B7280] mt-0.5">{l.actor_email || 'sistema'}{l.entity_id ? ` · ${l.entity_id.slice(0, 8)}` : ''}</div>
                    {l.metadata && Object.keys(l.metadata).length > 0 && (
                      <div className="mt-1 text-[11px] font-mono text-[#9CA3AF] truncate max-w-2xl">{JSON.stringify(l.metadata)}</div>
                    )}
                  </div>
                  <div className="text-[10.5px] text-[#9CA3AF] font-mono">{formatDateTime(l.created_at).split(',')[0]}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------- Tab 2: Eventos manuales -------------------- */

function ManualTab() {
  const [events, setEvents] = useState([]);
  const [systems, setSystems] = useState([]);
  const [activities, setActivities] = useState([]);
  const [meta, setMeta] = useState({ actions: {} });
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [ev, s, a, m] = await Promise.all([
        api.get('/trazabilidad/events?source=manual'),
        api.get('/systems'),
        api.get('/rat/activities'),
        api.get('/trazabilidad/meta'),
      ]);
      setEvents(ev.data); setSystems(s.data); setActivities(a.data); setMeta(m.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    try {
      await api.post('/trazabilidad/events', payload);
      toast.success('Evento registrado');
      setCreating(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const del = async (id) => {
    if (!window.confirm('¿Eliminar este evento?')) return;
    await api.delete(`/trazabilidad/events/${id}`);
    toast.success('Evento eliminado');
    await load();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[14px] font-medium text-[#1C2329]">
            {events.length} evento{events.length === 1 ? '' : 's'} manual{events.length === 1 ? '' : 'es'}
          </div>
          <div className="text-[12.5px] text-[#6B7280]">
            Registra aquí lo que ocurre <b>fuera</b> de Priviax (carpetas físicas, planillas, sistemas sin logs).
          </div>
        </div>
        <button className="pv-btn-primary flex items-center gap-2" onClick={() => setCreating(true)} data-testid="manual-new">
          <Plus size={14} /> Nuevo evento manual
        </button>
      </div>

      {loading ? <div className="text-sm text-[#6B7280]">Cargando…</div> :
       events.length === 0 ? (
        <div className="border border-[#E5E7EB] bg-white p-10 text-center">
          <ClipboardList size={26} className="mx-auto text-[#9CA3AF]" strokeWidth={1.5} />
          <div className="mt-3 text-lg font-semibold text-[#1C2329]">Aún no hay eventos manuales</div>
          <div className="text-sm text-[#6B7280] mt-1 max-w-md mx-auto">
            Ejemplos: entrega de expediente físico a RRHH, exportación de una planilla, destrucción de documentos.
          </div>
        </div>
      ) : (
        <div className="border border-[#E5E7EB] bg-white overflow-x-auto">
          <table className="pv-table" data-testid="manual-table">
            <thead><tr>
              <th style={{ width: 110 }}>Fecha</th>
              <th>Descripción</th>
              <th>Sistema</th>
              <th>Actividad RAT</th>
              <th>Responsable</th>
              <th style={{ width: 50 }}></th>
            </tr></thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.id} data-testid={`manual-row-${e.id}`}>
                  <td className="text-[12px] font-mono">{e.event_date?.slice(0, 10)}</td>
                  <td>
                    <div className="text-[13px] text-[#1C2329]">{e.description}</div>
                    <div className="text-[11px] text-[#6B7280] mt-0.5">
                      <span className="pv-chip"><span className="pv-chip-dot" />{meta.actions[e.action] || e.action}</span>
                    </div>
                  </td>
                  <td className="text-[12.5px]">{e.system_name || '—'}</td>
                  <td className="text-[12.5px]">{activities.find((a) => a.id === e.activity_id)?.name || '—'}</td>
                  <td className="text-[12.5px]">{e.responsible || '—'}</td>
                  <td>
                    <button onClick={() => del(e.id)} className="text-[#DC2626] hover:bg-[#FEF2F2] p-1 rounded">
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <ManualEventEditor
          systems={systems} activities={activities} meta={meta}
          onSave={save} onCancel={() => setCreating(false)}
        />
      )}
    </div>
  );
}

function ManualEventEditor({ systems, activities, meta, onSave, onCancel }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    event_date: today, system_id: '', system_name: '', activity_id: '',
    action: 'acceso', responsible: '', description: '',
  });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const canSave = form.event_date && form.description.trim().length > 3;

  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white border border-[#E5E7EB] max-w-2xl w-full max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="manual-editor">
        <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
          <div>
            <div className="text-[15px] font-semibold text-[#1C2329]">Nuevo evento manual</div>
            <div className="text-[12px] text-[#6B7280] mt-0.5">
              Registro fiel del acceso, modificación o comunicación de datos personales.
            </div>
          </div>
          <button onClick={onCancel} className="p-1.5 rounded hover:bg-[#F3F4F6] text-[#6B7280]"><X size={16} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <F label="Fecha">
              <input type="date" className="pv-input" value={form.event_date} onChange={set('event_date')} data-testid="me-date" />
            </F>
            <F label="Tipo de acción">
              <select className="pv-input" value={form.action} onChange={set('action')} data-testid="me-action">
                {Object.entries(meta.actions || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </F>
          </div>

          <F label="Sistema o proceso (del catastro)">
            <select className="pv-input" value={form.system_id} onChange={(e) => {
              const sys = systems.find((s) => s.id === e.target.value);
              setForm((f) => ({ ...f, system_id: e.target.value, system_name: sys?.name || '' }));
            }} data-testid="me-system">
              <option value="">— Seleccione o escriba abajo —</option>
              {systems.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            {!form.system_id && (
              <input className="pv-input mt-2" placeholder="…o nombre libre si no está en el catastro"
                value={form.system_name} onChange={set('system_name')} data-testid="me-system-free" />
            )}
          </F>

          <F label="Actividad de tratamiento (RAT) afectada (opcional)">
            <select className="pv-input" value={form.activity_id} onChange={set('activity_id')} data-testid="me-activity">
              <option value="">— No aplica —</option>
              {activities.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </F>

          <F label="Responsable">
            <input className="pv-input" value={form.responsible} onChange={set('responsible')}
              placeholder="Ej: María Pérez (RRHH)" data-testid="me-responsible" />
          </F>

          <F label="Descripción del evento">
            <textarea className="pv-input" rows={3} value={form.description} onChange={set('description')}
              placeholder="Ej: Se entregó expediente físico a la jefa de RRHH. Devuelto el mismo día."
              data-testid="me-description" />
            <div className="mt-2 flex flex-wrap gap-1.5">
              <span className="text-[11px] text-[#6B7280] pt-1 pr-1">Sugerencias:</span>
              {QUICK_DESCRIPTIONS.map((qd) => (
                <button key={qd} type="button" onClick={() => set('description')(qd)}
                  className="text-[11.5px] px-2 py-0.5 border border-[#E5E7EB] hover:border-[#143A34] hover:text-[#143A34] text-[#6B7280] rounded-sm">
                  {qd}
                </button>
              ))}
            </div>
          </F>
        </div>

        <div className="px-6 py-4 border-t border-[#E5E7EB] flex justify-between bg-white">
          <button className="pv-btn-secondary" onClick={onCancel}>Cancelar</button>
          <button className="pv-btn-primary" disabled={!canSave}
            onClick={() => onSave(form)} data-testid="me-save">
            <Check size={13} className="inline mr-1" /> Registrar evento
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------- Tab 3: Importar CSV -------------------- */

function CsvTab() {
  const [csv, setCsv] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [systems, setSystems] = useState([]);

  const loadImported = async () => {
    const [ev, s] = await Promise.all([
      api.get('/trazabilidad/events?source=csv'),
      api.get('/systems'),
    ]);
    setEvents(ev.data);
    setSystems(s.data);
  };
  useEffect(() => { loadImported(); }, []);

  const onFile = async (f) => {
    if (!f) return;
    const text = await f.text();
    setCsv(text);
    setResult(null);
  };

  const doImport = async () => {
    if (!csv.trim()) return toast.error('Sin CSV para importar');
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post('/trazabilidad/import-csv', { csv });
      setResult(data);
      toast.success(`${data.inserted} evento(s) importado(s)`);
      setCsv('');
      await loadImported();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error de importación');
    } finally { setLoading(false); }
  };

  const templateCsv = 'fecha,sistema,actividad,responsable,accion,descripcion\n' +
    '2026-08-03,Carpeta física RRHH,Personal interno,María Pérez,comunicacion,Se entregó expediente físico a RRHH; devuelto el mismo día.\n' +
    '2026-08-04,HubSpot,Base de clientes B2B,Juan Soto,exportacion,Exportación de listado de contactos para campaña.\n';

  const downloadTemplate = () => {
    const blob = new Blob([templateCsv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'trazabilidad_plantilla.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="border border-[#143A34]/15 bg-[#F0FDF4] p-4 flex items-start gap-2.5 text-[13px] text-[#065F46]" data-testid="csv-info">
        <Info size={15} className="mt-0.5 shrink-0" />
        <div>
          <div className="font-medium">Formato esperado</div>
          <div className="mt-1">Columnas: <code>fecha, sistema, actividad, responsable, accion, descripcion</code>. Fecha y descripción son obligatorias. Sistema y actividad se enlazan automáticamente por nombre si existen en el catastro / RAT.</div>
          <button onClick={downloadTemplate} className="mt-2 text-[12px] underline hover:no-underline" data-testid="csv-template">
            Descargar plantilla CSV
          </button>
        </div>
      </div>

      <div className="border border-dashed border-[#D1D5DB] bg-white p-6 text-center">
        <Database size={22} className="mx-auto text-[#9CA3AF]" strokeWidth={1.5} />
        <div className="mt-2 text-[13px] text-[#374151]">Sube un archivo <b>.csv</b> con eventos pasados</div>
        <label className="pv-btn-primary inline-flex items-center gap-2 mt-3 cursor-pointer" data-testid="csv-upload">
          <Upload size={14} /> Elegir archivo
          <input type="file" accept=".csv,text/csv" className="hidden"
            onChange={(e) => onFile(e.target.files?.[0])} />
        </label>
        <div className="text-[11px] text-[#9CA3AF] mt-2">o pega el contenido debajo</div>
      </div>

      <textarea className="pv-input font-mono text-[12px]" rows={7}
        placeholder="fecha,sistema,actividad,responsable,accion,descripcion..."
        value={csv} onChange={(e) => setCsv(e.target.value)} data-testid="csv-textarea" />

      <div className="flex justify-end">
        <button className="pv-btn-primary flex items-center gap-2"
          disabled={!csv.trim() || loading}
          onClick={doImport} data-testid="csv-import">
          <Upload size={14} /> {loading ? 'Importando…' : 'Importar eventos'}
        </button>
      </div>

      {result && (
        <div className="border border-[#E5E7EB] bg-white p-4" data-testid="csv-result">
          <div className="text-[13px]">
            <span className="text-[#065F46] font-medium">{result.inserted}</span> evento(s) importado(s)
            {result.errors?.length > 0 && (
              <>, <span className="text-[#92400E]">{result.errors.length}</span> con errores</>
            )}.
          </div>
          {result.errors?.length > 0 && (
            <ul className="mt-2 space-y-1 text-[11.5px] text-[#92400E]">
              {result.errors.slice(0, 20).map((e, i) => (
                <li key={i}>· Fila {e.row}: {e.message}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {events.length > 0 && (
        <div>
          <div className="text-[13px] font-medium text-[#1C2329] mb-2">Eventos importados por CSV ({events.length})</div>
          <div className="border border-[#E5E7EB] bg-white overflow-x-auto">
            <table className="pv-table" data-testid="csv-imported-table">
              <thead><tr>
                <th style={{ width: 110 }}>Fecha</th>
                <th>Descripción</th>
                <th>Sistema</th>
                <th>Responsable</th>
              </tr></thead>
              <tbody>
                {events.slice(0, 50).map((e) => (
                  <tr key={e.id}>
                    <td className="text-[12px] font-mono">{e.event_date?.slice(0, 10)}</td>
                    <td className="text-[13px]">{e.description}</td>
                    <td className="text-[12.5px]">
                      {e.system_name || '—'}
                      {e.system_id && !systems.find((s) => s.id === e.system_id) && (
                        <span className="ml-1 text-[10px] text-[#92400E]">(sistema eliminado)</span>
                      )}
                    </td>
                    <td className="text-[12.5px]">{e.responsible || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------- shared -------------------- */

function FilterChip({ active, onClick, children }) {
  return (
    <button onClick={onClick} className={`text-[12px] px-2.5 py-1 border ${active ? 'bg-[#0B1220] text-white border-[#0B1220]' : 'bg-white text-[#374151] border-[#E5E7EB] hover:bg-[#FAFBFC]'}`}>
      {children}
    </button>
  );
}

function F({ label, children }) {
  return <div><label className="block text-[12px] font-medium text-[#374151] mb-1.5">{label}</label>{children}</div>;
}
